import { AxiosError } from 'axios'
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import type { AnyRouter } from '@tanstack/react-router'
import { toast } from 'sonner'
import { useAuthStore } from '@/stores/auth-store'

// A wrong OTP code (or wrong login) also comes back as 401, but it isn't a dead
// session — it's an expected validation response the form itself should show and
// let the user retry, not a reason to log them out mid-flow.
const isPreAuthEndpoint = (error: AxiosError) => error.config?.url?.includes('/otp/')

/** Whatever the API said, when it said anything at all. */
const serverMessage = (error: AxiosError): string | null => {
  const data = error.response?.data as { message?: unknown } | undefined
  const message = typeof data?.message === 'string' ? data.message.trim() : ''

  return message === '' ? null : message
}

/**
 * One place decides what a failed request looks like to the person.
 *
 * A 403 used to print the same sentence whatever happened — «دسترسی این
 * عملیات را ندارید» — which is right for a missing permission and a lie
 * everywhere else. The API answers a refusal with a reason a person can
 * act on ("enter your password first"), so that reason is what shows;
 * the old sentence stays as the fallback for a refusal that came with
 * no words of its own.
 */
function handleApiError(error: unknown, getRouter: () => AnyRouter): void {
  if (!(error instanceof AxiosError)) {
    return
  }

  const status = error.response?.status

  if (status === 401 && !isPreAuthEndpoint(error)) {
    const router = getRouter()
    useAuthStore.getState().logout()
    const redirect = `${router.history.location.href}`
    router.navigate({ to: '/sign-in', search: { redirect } })

    return
  }

  if (status === 500) {
    toast.error('خطایی سمت سرور رخ داده است')

    return
  }

  if (status === 403) {
    toast.error(serverMessage(error) ?? 'دسترسی این عملیات را ندارید')

    return
  }

  toast.error(serverMessage(error) ?? 'خطایی رخ داده است')
}

export function createQueryClient(getRouter: () => AnyRouter) {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: false,
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000, // 5 minutes
      },
    },
    mutationCache: new MutationCache({
      onError: (error) => handleApiError(error, getRouter),
      onSuccess: (data, _variables, _context, mutation) => {
        if (mutation.meta?.showToastOnSuccess) {
          // Show custom message or default
          const message =
            typeof mutation.meta.successMessage === 'string'
              ? mutation.meta.successMessage
              : typeof data === 'object' && data !== null && 'message' in data
                ? (data?.message as string)
                : 'عملیات با موفقیت انجام شد'
          toast.success(message)
        }
      },
    }),
    queryCache: new QueryCache({
      onError: (error) => handleApiError(error, getRouter),
    }),
  })
}
